import {
  type CrossClientQueryClauseResolverMapping,
  type CrossClientQueryClientQueryMode,
  type CrossClientQueryDataSource,
  type CrossClientQueryLogicalQuestionDecomposition,
  type CrossClientQueryLogicalQuestionNNFDecomposition,
} from './CrossClient'
import { type Identity } from './User'

export const ScheduledCrossClientQueryAudienceTypes = {
  CREATOR: 'creator',
  ALL_USERS: 'allUsers',
  USER_SUBSET: 'userSubset',
} as const

export type ScheduledCrossClientQueryAudienceType = (typeof ScheduledCrossClientQueryAudienceTypes)[keyof typeof ScheduledCrossClientQueryAudienceTypes]

export interface CreatorAudience {
  type: typeof ScheduledCrossClientQueryAudienceTypes.CREATOR
}

export interface AllUsersAudience {
  type: typeof ScheduledCrossClientQueryAudienceTypes.ALL_USERS
}

export interface UserSubsetAudience {
  type: typeof ScheduledCrossClientQueryAudienceTypes.USER_SUBSET
  userIds: string[]
}

export type ScheduledCrossClientQueryAudience = CreatorAudience | AllUsersAudience | UserSubsetAudience

export interface CrossClientQuerySchedule {
  cronExpressions: string[]
}

export interface ScheduledCrossClientQueryAutomation {
  findNewMatches: boolean
  confirmPotentialMatchesWithIntegrations: boolean
  confirmPotentialMatchesWithFullReview: boolean
  aiBatchSize?: number | null
}

export const DEFAULT_AI_BATCH_SIZE = 20

export const DEFAULT_SCHEDULED_AUTOMATION: ScheduledCrossClientQueryAutomation = {
  findNewMatches: true,
  confirmPotentialMatchesWithIntegrations: true,
  confirmPotentialMatchesWithFullReview: true,
  aiBatchSize: DEFAULT_AI_BATCH_SIZE,
}

export const DISABLED_SCHEDULED_AUTOMATION: ScheduledCrossClientQueryAutomation = {
  findNewMatches: false,
  confirmPotentialMatchesWithIntegrations: false,
  confirmPotentialMatchesWithFullReview: false,
  aiBatchSize: null,
}

export const normalizeScheduledAutomation = (automation: ScheduledCrossClientQueryAutomation): ScheduledCrossClientQueryAutomation => ({
  ...automation,
  aiBatchSize: automation.confirmPotentialMatchesWithFullReview ? (automation.aiBatchSize ?? DEFAULT_AI_BATCH_SIZE) : null,
})

export interface ScheduledCrossClientQueryDefinition {
  sources: CrossClientQueryDataSource[]
  decomposition: CrossClientQueryLogicalQuestionDecomposition
  nnfDecomposition?: CrossClientQueryLogicalQuestionNNFDecomposition | null
  clauseResolverMapping?: CrossClientQueryClauseResolverMapping | null
  localClauseResolverMapping?: CrossClientQueryClauseResolverMapping | null
  resolvableClauseVariables?: string[] | null
  localResolvableClauseVariables?: string[] | null
  userQuestion: string
  clientQueryMode?: CrossClientQueryClientQueryMode | null
  sourceExecutionId?: string | null
}

export interface ScheduledCrossClientQuery {
  id: string
  name: string
  active: boolean
  schedule: CrossClientQuerySchedule
  audience: ScheduledCrossClientQueryAudience
  definition: ScheduledCrossClientQueryDefinition
  automation?: ScheduledCrossClientQueryAutomation | null
  createdAt: string
  updatedAt: string
  createdBy: Identity
  updatedBy: Identity
  managedBy: Identity
}

export interface ScheduledCrossClientQuerySummary {
  id: string
  name: string
  active: boolean
  schedule: CrossClientQuerySchedule
  userQuestion: string
  audience: ScheduledCrossClientQueryAudience
  createdAt: string
  updatedAt: string
  createdBy: Identity
  updatedBy: Identity
  managedBy: Identity
}

export interface DirectScheduledCrossClientQueryDefinitionCreate {
  type: 'direct'
  sources: CrossClientQueryDataSource[]
  decomposition: CrossClientQueryLogicalQuestionDecomposition
  nnfDecomposition: CrossClientQueryLogicalQuestionNNFDecomposition | null
  clauseResolverMapping?: CrossClientQueryClauseResolverMapping | null
  localClauseResolverMapping?: CrossClientQueryClauseResolverMapping | null
  resolvableClauseVariables?: string[] | null
  localResolvableClauseVariables?: string[] | null
  userQuestion: string
  clientQueryMode?: CrossClientQueryClientQueryMode | null
}

export interface FromExecutionScheduledCrossClientQueryDefinitionCreate {
  type: 'fromExecution'
  sourceExecutionId: string
}

export type ScheduledCrossClientQueryDefinitionCreate = DirectScheduledCrossClientQueryDefinitionCreate | FromExecutionScheduledCrossClientQueryDefinitionCreate

export interface ScheduledCrossClientQueryCreate {
  name: string
  active?: boolean
  schedule: CrossClientQuerySchedule
  definition: ScheduledCrossClientQueryDefinitionCreate
  automation?: ScheduledCrossClientQueryAutomation | null
  userId?: string | null
}

export interface ScheduledCrossClientQueryDefinitionUpdate {
  sources?: CrossClientQueryDataSource[]
  decomposition?: CrossClientQueryLogicalQuestionDecomposition
  nnfDecomposition?: CrossClientQueryLogicalQuestionNNFDecomposition | null
  clauseResolverMapping?: CrossClientQueryClauseResolverMapping | null
  localClauseResolverMapping?: CrossClientQueryClauseResolverMapping | null
  userQuestion?: string
  clientQueryMode?: CrossClientQueryClientQueryMode | null
}

export interface ScheduledCrossClientQueryUpdate {
  name?: string
  active?: boolean
  schedule?: CrossClientQuerySchedule
  definition?: ScheduledCrossClientQueryDefinitionUpdate
  automation?: ScheduledCrossClientQueryAutomation | null
}
